import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { resolveTimeGranularity } from "app/components/widget/widget-utilities/time-granularity";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";

interface IUseTotalVisitsPeakQueryData {
    yearOverYearChangeOverThresholdKeys: string[];
    trendReversedKeys: string[];
    seasonalPeaksKeys: string[];
}

const emptyData: IUseTotalVisitsPeakQueryData = {
    yearOverYearChangeOverThresholdKeys: [],
    trendReversedKeys: [],
    seasonalPeaksKeys: [],
};

const api = "/widgetApi/WebsiteOverview/EngagementVisits/ChangePatterns";

interface IUseTotalVisitsPeakQueryProps {
    hasTotalVisitsHook: boolean;
}

export const useTotalVisitsPeakQuery = ({ hasTotalVisitsHook }: IUseTotalVisitsPeakQueryProps) => {
    const { params } = useCurrentStateAndParams();
    const { country, key, isWWW, webSource, duration } = params;
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;

    const timeGranularity = resolveTimeGranularity(duration);
    const includeSubDomains = isWWW === "*";

    const fetchService = DefaultFetchService.getInstance();

    const queryParams = useMemo(
        () => ({
            country,
            from,
            to,
            keys: key,
            timeGranularity,
            includeSubDomains,
            isWindow,
            webSource,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ShouldGetVerifiedData: false,
        }),
        [country, from, includeSubDomains, isWindow, key, timeGranularity, to, webSource],
    );

    const query = useQuery<IUseTotalVisitsPeakQueryData>({
        queryKey: [queryParams],
        queryFn: async () => {
            return await fetchService.get(api, queryParams);
        },
        enabled: hasTotalVisitsHook,
    });

    if (!hasTotalVisitsHook) {
        return {
            isLoading: false,
            data: emptyData,
        };
    }

    return query;
};
